import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Document } from '../types';

interface DocumentDeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: Document | null;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DocumentDeleteConfirmDialog({
  open,
  onOpenChange,
  document,
  onConfirm,
  isDeleting = false,
}: DocumentDeleteConfirmDialogProps) {
  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Permanently Delete Document
          </DialogTitle>
          <DialogDescription className="text-left">
            This document is already marked as private. Deleting it will permanently remove it from the system along with all related data.
          </DialogDescription>
        </DialogHeader>
        
        <div className="my-4 p-4 bg-muted rounded-lg">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-medium">Document:</span>
              <span className="text-sm">{document.title}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">ID:</span>
              <span className="text-sm font-mono">{document.id}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Status:</span>
              <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                Private
              </Badge>
            </div>
          </div>
        </div>

        <div className="space-y-3 text-sm text-muted-foreground">
          <p className="font-medium text-destructive">⚠️ This action will permanently delete:</p>
          <ul className="list-disc list-inside space-y-1 pl-4">
            <li>The document record</li>
            <li>All associated media files</li>
            <li>Document tags and categories</li>
            <li>All related references (document_media, document_tag, etc.)</li>
            <li>Document history and audit logs</li>
          </ul>
          <p className="font-medium text-destructive">
            This action cannot be undone.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
